// Import React helpers
import React, { useEffect, useRef, useState } from "react";

// Import portal helper
import { createPortal } from "react-dom";



// Get the floating UI root
const GetFloatingUiRoot = () => {

    // Find the floating UI root
    const floatingRoot = document.getElementById("floating-ui-root");

    // Return the floating root if it exists
    if (floatingRoot) {

        // Return the floating root
        return floatingRoot;

    }

    // Fallback to document body
    return document.body;

};



// Export reusable portal popover
export const PortalPopover = ({
    anchorRef,
    isOpen,
    onClose,
    children,
    className = "",
    offset = 8,
    innerRef = null,
    onScroll = null,
}) => {


    // Create popover ref
    const popoverRef = useRef(null);

    
    // Store popover position
    const [popoverPosition, setPopoverPosition] = useState({
        top: 0,
        left: 0,
        visibility: "hidden",
    });



    // Close when clicking outside anchor and popover
    useEffect(() => {

        // Do nothing when closed
        if (!isOpen) {

            // Early return
            return;

        }

        // Handle outside mouse down
        const handleDocumentMouseDown = (event) => {

            // Check if click is inside anchor
            const clickedInsideAnchor = anchorRef.current?.contains(event.target);

            // Check if click is inside popover
            const clickedInsidePopover = popoverRef.current?.contains(event.target);

            // Ignore clicks inside anchor or popover
            if (clickedInsideAnchor || clickedInsidePopover) {

                // Early return
                return;

            }

            // Close if handler exists
            if (typeof onClose === "function") {

                // Call close handler
                onClose();

            }

        };

        // Add listener
        document.addEventListener("mousedown", handleDocumentMouseDown);

        // Cleanup listener
        return () => {

            // Remove listener
            document.removeEventListener("mousedown", handleDocumentMouseDown);

        };

    }, [anchorRef, isOpen, onClose]);



    // Position the popover
    useEffect(() => {

        // Do nothing when closed
        if (!isOpen) {

            // Early return
            return;

        }

        // Update position
        const updatePopoverPosition = () => {

            // Do nothing without anchor
            if (!anchorRef.current) {

                // Early return
                return;

            }

            // Get anchor position
            const anchorRect = anchorRef.current.getBoundingClientRect();

            // Get popover position
            const popoverRect = popoverRef.current?.getBoundingClientRect();

            // Set viewport padding
            const viewportPadding = 8;

            // Get width fallback
            const popoverWidth = popoverRect?.width || 240;

            // Get height fallback
            const popoverHeight = popoverRect?.height || 120;

            // Start below the anchor
            let nextTop = anchorRect.bottom + offset;

            // Align left with anchor
            let nextLeft = anchorRect.left;

            // Prevent right overflow
            if (nextLeft + popoverWidth > window.innerWidth - viewportPadding) {

                // Shift left
                nextLeft = window.innerWidth - popoverWidth - viewportPadding;

            }

            // Prevent left overflow
            if (nextLeft < viewportPadding) {

                // Shift right
                nextLeft = viewportPadding;

            }

            // Prevent bottom overflow
            if (nextTop + popoverHeight > window.innerHeight - viewportPadding) {

                // Move above anchor
                nextTop = anchorRect.top - popoverHeight - offset;

            }

            // Prevent top overflow
            if (nextTop < viewportPadding) {

                // Keep inside viewport
                nextTop = viewportPadding;

            }

            // Save position
            setPopoverPosition({
                top: nextTop,
                left: nextLeft,
                visibility: "visible",
            });

        };

        // Position immediately
        updatePopoverPosition();

        // Position after render
        requestAnimationFrame(updatePopoverPosition);

        // Update on resize
        window.addEventListener("resize", updatePopoverPosition);

        // Update on scroll
        window.addEventListener("scroll", updatePopoverPosition, true);

        // Cleanup
        return () => {

            // Remove resize listener
            window.removeEventListener("resize", updatePopoverPosition);

            // Remove scroll listener
            window.removeEventListener("scroll", updatePopoverPosition, true);

        };

    }, [anchorRef, isOpen, offset]);



    // Do not render when closed
    if (!isOpen) {

        // Return nothing
        return null;

    }



    // Return portal
    return createPortal(

        <div
            ref={(element) => {

                // Store this element for PortalPopover positioning and outside-click checks.
                popoverRef.current = element;

                // Forward the element to the caller ref if provided.
                if (innerRef) {

                    // Set the caller ref.
                    innerRef.current = element;

                }

            }}
            className={className}
            onScroll={onScroll}
            style={{
                position: "fixed",
                top: `${popoverPosition.top}px`,
                left: `${popoverPosition.left}px`,
                visibility: popoverPosition.visibility,
                zIndex: 1002,
            }}
        >

            {children}

        </div>,

        GetFloatingUiRoot()

    );

};